import { BLOCK, NONE } from "../../library/constants";
import { ToolbarOptions } from "@zebrabi/global-toolbar-old/interface/ToolbarOption";
import type { TablesToolbarHost } from "../TablesToolbarHost";
import type { VarianceSettings } from "../../settings/varianceSettings";
import { flagHandler } from "@zebrabi/zebrabi-core";
import { getOfficeSettings } from "@zebrabi/office-settings";
import { DATA_SOURCE, type DataSource, createDialog } from "@zebrabi/data-helpers/editData";
import { FIELDS_LIST_FLAG, MeasureRoles, CategoricalRoles, type FieldRole } from "@zebrabi/office-types";
import { getColumnRole } from "@zebrabi/data-helpers/visualHelpers";
import { getMaxRoleCount } from "@zebrabi/data-helpers/tablesMetadata";
import { fieldsListOf, sourceIndexOf, valueColumnKind, type ReaderField } from "@zebrabi/data-helpers/sourceColumns";
import type { DataViewMetadataColumn } from "@zebrabi/matrix-data";
import type {
    BucketDefinition,
    Field,
    FieldsPane,
    FieldsPaneReorderEvent,
    FieldsPaneRolesChangeEvent,
    RoleChange,
} from "@zebrabi/fields-pane";
import BaseSwitcherWithHeaderOld from "./BaseSwitcherWithHeaderOld";
import type { ColumnOptions, ScenarioOptions } from "../../definitions";
import { isPlanScenario, isForecastScenario } from "../../helpers";

/** Category and Group are display only, so `maxFields` never applies; Comments holds a text column, the other value buckets numbers */
const BUCKETS: BucketDefinition[] = [
    { role: CategoricalRoles.Category, label: "Category", accepts: [], maxFields: Infinity, reorderable: false },
    { role: CategoricalRoles.Group, label: "Group", accepts: [], maxFields: Infinity, reorderable: false },
    {
        role: MeasureRoles.Values,
        label: "Actual",
        accepts: ["measure"],
        maxFields: getMaxRoleCount(MeasureRoles.Values),
        reorderable: false,
    },
    {
        role: MeasureRoles.PreviousYear,
        label: "Previous Year",
        accepts: ["measure"],
        maxFields: getMaxRoleCount(MeasureRoles.PreviousYear),
        reorderable: false,
    },
    { role: MeasureRoles.Plan, label: "Plan", accepts: ["measure"], maxFields: getMaxRoleCount(MeasureRoles.Plan), reorderable: true },
    {
        role: MeasureRoles.Forecast,
        label: "Forecast",
        accepts: ["measure"],
        maxFields: getMaxRoleCount(MeasureRoles.Forecast),
        reorderable: true,
    },
    {
        role: MeasureRoles.Comments,
        label: "Comments",
        accepts: ["text"],
        maxFields: getMaxRoleCount(MeasureRoles.Comments),
        reorderable: false,
    },
];

/**
 * The Comments bucket takes text only once the cells are known. A pivot table and an older document list no cells,
 * so every value column there is a number and the Comments bucket has to take one, or nothing could ever enter it.
 */
function bucketsFor(missingDataCells: boolean): BucketDefinition[] {
    return BUCKETS.map((bucket: BucketDefinition): BucketDefinition => {
        const isComments = bucket.role === MeasureRoles.Comments;
        const takesNumbers = isComments && missingDataCells;

        return takesNumbers ? { ...bucket, accepts: ["measure", "text"] } : bucket;
    });
}

/** The Data fields toolbar entry: mounts the shared pane and commits what the user does in it to the settings */
class FieldsTablesSwitcher extends BaseSwitcherWithHeaderOld {
    static readonly CLASS_NAME = `FieldsTablesSwitcher`;

    toolbarOptions: ToolbarOptions = {
        collapsed: true,
        elementName: "Data fields",
        icon: "fields-icon-base64",
        type: "button",
        actions: [],
    };

    /** The pane of the current menu render; refreshed after each commit so it shows the new state even if the host skips the redraw */
    private pane: FieldsPane | undefined;

    /** The flag as read at the last menu render, so a refresh after a commit builds the same pane the render did */
    private fieldsListFlag = false;

    constructor(private readonly host: TablesToolbarHost<VarianceSettings, ScenarioOptions>) {
        super();
    }

    /**
     * Transpiling will lose the type of the class once minimised, method is used the type is preserved
     */
    public getClassName(): string {
        return FieldsTablesSwitcher.CLASS_NAME;
    }

    /** Required by the base class; this switcher declares no menu actions, so it is never called */
    buttonAction(): void {}

    /** The toolbar's broadcast; the entry hides in viewer mode */
    update(message: Map<string, unknown>): void {
        if (message.has("viewer")) {
            if (message.get("viewer") === "true") {
                this.switcherContainer.style.display = NONE;
            } else {
                this.switcherContainer.style.display = BLOCK;
            }
        }
    }

    /**
     * Mounts the shared pane with the current columns. Runs on every menu render, and a committed change re-renders the
     * menu, so the pane is rebuilt rather than updated in place; a flag that flips mid-session takes effect at the next render.
     * @param switcherMenuContainer the menu element the old toolbar hands every switcher; the pane goes into its `.body`
     * @param active the toolbar's active-item marker, passed through to the base class and unused here
     */
    createMenuItems(switcherMenuContainer: HTMLElement, active: string = "") {
        super.createMenuItems(switcherMenuContainer, active);
        const body = switcherMenuContainer.querySelector(".body");
        const form = document.createElement("div");
        form.classList.add("fields-settings-form-2");
        this.fieldsListFlag = flagHandler.has(FIELDS_LIST_FLAG);

        const pane = document.createElement("fields-pane");
        this.pane = pane;
        pane.buckets = bucketsFor(this.host.currentDataView()?.sourceColumns === undefined);
        pane.fields = this.buildFields();
        pane.fieldsList = this.showsFieldsList();
        // the list only shows the assignment: nothing stores a column taken out of a bucket or put into Category or Group
        pane.fieldsListReadOnly = true;
        pane.dragFeedback = this.fieldsListFlag;
        pane.dataSource = this.currentDataSource();
        pane.addEventListener("fields-pane-roles-change", (event: FieldsPaneRolesChangeEvent) =>
            this.applyRoleChanges(event.detail.changes)
        );
        pane.addEventListener("fields-pane-reorder", (event: FieldsPaneReorderEvent) =>
            this.reorderScenario(event.detail.fieldId, event.detail.role, event.detail.index)
        );
        pane.addEventListener("fields-pane-edit-data-source", () => this.editDataSource());

        form.appendChild(pane);
        body?.appendChild(form);
    }

    /** The list needs the source's columns, which a pivot table and a document stored before they were parsed do not have */
    private showsFieldsList(): boolean {
        return this.fieldsListFlag && this.host.currentDataView()?.sourceColumns !== undefined;
    }

    /**
     * The reader's columns as pane fields; Plan and Forecast in the order of their scenario option positions. With the
     * Fields list on, the same fields laid over the source columns, so the list also shows the columns the reader skipped.
     */
    private buildFields(): Field[] {
        const columns = this.columns();
        const sourceColumns = this.host.currentDataView()?.sourceColumns;

        // a flat source reads its leading columns as the category levels, in order, and the rest as value fields
        const categoryColumnsCount = columns.filter((column: DataViewMetadataColumn) => column.roles[CategoricalRoles.Category]).length;
        let categoryOrdinal = 0;

        const readerFields = columns.flatMap((column: DataViewMetadataColumn): ReaderField[] => {
            const role = getColumnRole(column);
            if (!role) {
                return [];
            }
            // levels, and the synthetic "Values" of a cross table, are shown but stay where they are
            const movable = column.index !== undefined && isMeasureRole(role);
            const sourceIndex =
                role === CategoricalRoles.Category ? categoryOrdinal++ : sourceIndexOf(column, sourceColumns, categoryColumnsCount);
            // a level is text; a value column is what its cells say
            const kind = isMeasureRole(role) ? valueColumnKind(sourceIndex, sourceColumns) : "text";
            const field: Field = { id: fieldId(column), label: column.displayName, kind, role, movable };
            return [{ field, sourceIndex }];
        });

        const fields =
            sourceColumns && this.showsFieldsList()
                ? fieldsListOf(sourceColumns, readerFields)
                : readerFields.map((readerField: ReaderField) => readerField.field);

        return orderByScenarioPosition(fields, columns, this.host.currentScenarioOptions());
    }

    private columns(): DataViewMetadataColumn[] {
        return this.host.currentDataView()?.metadata.columns ?? [];
    }

    /** No block in PowerPoint; in Excel the block shows the source, or nothing until one is chosen */
    private currentDataSource(): DataSource | null | undefined {
        if (Office.context.host !== Office.HostType.Excel) {
            return undefined;
        }
        return (getOfficeSettings(DATA_SOURCE) as DataSource | undefined) ?? null;
    }

    /**
     * Writes the measure roles the gesture changed. A move out of every bucket (`null`) and a move into Category or
     * Group are not stored, so they are skipped and the refresh puts the field back.
     */
    private applyRoleChanges(changes: RoleChange[]): void {
        const columns = this.columns();
        const returnData = new Map<string, MeasureRoles[]>();
        const planOrForecast = [MeasureRoles.Plan, MeasureRoles.Forecast];
        let touchesPlanOrForecast = false;

        changes.forEach((change: RoleChange) => {
            const column = columns.find((candidate: DataViewMetadataColumn) => fieldId(candidate) === change.fieldId);
            const role = change.role;
            if (!column || column.index === undefined || !isMeasureRole(role)) {
                return;
            }
            touchesPlanOrForecast ||= planOrForecast.includes(role) || planOrForecast.includes(getColumnRole(column) as MeasureRoles);
            this.setNewMeasureRoleSettings(role, column.index, returnData);
            column.roles = { [role]: true };
        });

        if (touchesPlanOrForecast) {
            this.host.onPlanForecastRoleChanged();
        }
        this.refreshPane();
        this.notify(returnData, `${this.getClassName()}Observer`);
    }

    /** Swaps the dropped field's position with the field already at the target index of the same scenario kind */
    private reorderScenario(droppedFieldId: string, role: FieldRole, index: number): void {
        const scenarioOptions = this.host.currentScenarioOptions();
        const column = this.columns().find((candidate: DataViewMetadataColumn) => fieldId(candidate) === droppedFieldId);
        const option = scenarioOptions && column ? scenarioOptionFor(column, scenarioOptions) : undefined;
        if (!scenarioOptions || !option) {
            return;
        }
        const isSameKind = role === MeasureRoles.Plan ? isPlanScenario : isForecastScenario;
        const atTarget = scenarioColumnOptions(scenarioOptions).find(
            (candidate: ColumnOptions) => candidate.position === index && isSameKind(candidate.scenario)
        );
        if (!atTarget) {
            return;
        }
        atTarget.position = option.position;
        option.position = index;
        // the positions live in the scenario options, so the observer only has to persist and redraw
        this.refreshPane();
        this.notify(new Map(), `${this.getClassName()}Observer`);
    }

    /** The pane renders a tick after the menu, so the toolbar's restore lands on an empty body; apply it again once rendered */
    public override setScrollPosition(scrollPosition: number): void {
        super.setScrollPosition(scrollPosition);
        void this.pane?.updateComplete.then(() => super.setScrollPosition(scrollPosition));
    }

    private refreshPane(): void {
        if (this.pane) {
            this.pane.fields = this.buildFields();
        }
    }

    private editDataSource(): void {
        const dataSource = getOfficeSettings(DATA_SOURCE) as DataSource | undefined;
        if (dataSource) {
            createDialog(dataSource, () => this.host.onDataSourceChanged());
        }
    }

    /** Writes the role of the column at `index` and puts the roles array into the observer message */
    private setNewMeasureRoleSettings(role: MeasureRoles, index: number, returnData: Map<string, MeasureRoles[]>): void {
        const settings = this.host.currentSettings();
        if (settings && index < settings.measureRoles.length) {
            settings.measureRoles[index] = role;
            returnData.set("measureRoles", settings.measureRoles);
        }
    }
}

/** A field with no bucket (`null`) or in Category or Group has no measure role to write */
function isMeasureRole(role: FieldRole | null): role is MeasureRoles {
    return role !== null && role in MeasureRoles;
}

/** Measures are identified by column index, levels by name (they carry no index) */
function fieldId(column: DataViewMetadataColumn): string {
    return column.index === undefined ? `level:${column.displayName}` : String(column.index);
}

/** The scenario slots that hold one column each; the array-valued ones (additional measures, tooltips, comments) carry no position */
const SCENARIO_SLOTS = [
    "value",
    "reference",
    "secondReference",
    "thirdReference",
    "fourthReference",
    "fifthReference",
    "sixthReference",
    "seventhReference",
] as const;
type ScenarioSlot = (typeof SCENARIO_SLOTS)[number];

function scenarioColumnOptions(scenarioOptions: ScenarioOptions): ColumnOptions[] {
    return SCENARIO_SLOTS.map((slot: ScenarioSlot) => scenarioOptions[slot]).filter(
        (option: ColumnOptions | undefined): option is ColumnOptions => !!option
    );
}

function scenarioOptionFor(column: DataViewMetadataColumn, scenarioOptions: ScenarioOptions): ColumnOptions | undefined {
    return scenarioColumnOptions(scenarioOptions).find(
        (option: ColumnOptions) => option.fieldName === column.displayName && option.index === column.index
    );
}

/** Plan and Forecast fields take the order of their scenario option positions; everything else keeps the column order */
function orderByScenarioPosition(
    fields: Field[],
    columns: DataViewMetadataColumn[],
    scenarioOptions: ScenarioOptions | undefined
): Field[] {
    if (!scenarioOptions) {
        return fields;
    }
    const position = new Map<string, number>();
    columns.forEach((column: DataViewMetadataColumn) => {
        const option = scenarioOptionFor(column, scenarioOptions);
        if (option?.position !== undefined) {
            position.set(fieldId(column), option.position);
        }
    });
    const ordered = [...fields];
    [MeasureRoles.Plan, MeasureRoles.Forecast].forEach((role: MeasureRoles) => {
        const slots = ordered
            .map((field: Field, index: number) => (field.role === role ? index : -1))
            .filter((index: number) => index >= 0);
        const sorted = slots
            .map((index: number) => ordered[index])
            .sort((a: Field, b: Field) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0));
        slots.forEach((slot: number, i: number) => (ordered[slot] = sorted[i]));
    });
    return ordered;
}

export default FieldsTablesSwitcher;
